import { urlRegex } from '@/constants/validation'
import { getPlainTextLength } from '@/utils/helper'
import z from 'zod'

export const API_SPONSORSHIP_STATUS = {
  PENDING: 'pending' as const,
  ACTIVE: 'active' as const,
  FAILED: 'failed' as const,
  CANCELLED: 'cancelled' as const,
  UNDER_PROCESS: 'under_process' as const,
}

export const DISPLAY_SPONSORSHIP_STATUS = {
  PAID: 'Paid' as const,
  SPONSORSHIP_REQUESTED: 'Sponsorship Requested' as const,
  NOT_SELECTED: 'Not Selected' as const,
  FAILED: 'Failed' as const,
  CANCELLED: 'Cancelled' as const,
}

export const API_SPONSORSHIP_EVENT_LOCATION = {
  ONLINE: 'online' as const,
  OFFLINE: 'offline' as const,
  HYBRID: 'hybrid' as const,
}

export const AdvertisementStatus = {
  ACTIVE: 'active' as const,
  INACTIVE: 'inactive' as const,
  EXPIRED: 'expired' as const,
  DRAFT: 'draft' as const,
  REJECTED: 'rejected' as const,
}

export const EVENT_STATUS = {
  ACTIVE: 'active',
  COMPLETED: 'closed',
} as const

export type EventStatusType = (typeof EVENT_STATUS)[keyof typeof EVENT_STATUS]

export const DISPLAY_EVENT_STATUS = {
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
} as const

export const sponsorShipPlanSchema = z.object({
  id: z.string(),
  name: z.string(),
  key: z.string(),
})

export const sponsorShipAPIStatusSchema = z.enum(
  Object.values(API_SPONSORSHIP_STATUS)
)

export const sponsorEventListSchema = z.object({
  id: z.string(),
  status: sponsorShipAPIStatusSchema,
  event: z.object({
    id: z.string(),
    name: z.string(),
    startDateTime: z.date(),
    endDateTime: z.date(),
    venueName: z.string(),
    venueAddress: z.string(),
    venueCity: z.string(),
    venueState: z.string(),
    venueCountry: z.string(),
    status: z.enum(Object.values(EVENT_STATUS)),
  }),
  plan: sponsorShipPlanSchema.nullable(),
})

export const sponsorEventListDisplaySchema = z.object({
  id: z.string(),
  eventName: z.string(),
  sponsorshipTier: z.string(),
  startDateTime: z.string(),
  endDateTime: z.string(),
  location: z.string(),
  sponsorshipStatus: z.enum(Object.values(DISPLAY_SPONSORSHIP_STATUS)),
  eventStatus: z.enum(Object.values(EVENT_STATUS)),
})

// Schema for sub-event sponsorship list
export const sponsorSubEventListSchema = z.object({
  id: z.string(),
  status: sponsorShipAPIStatusSchema,
  subEvent: z.object({
    id: z.string(),
    name: z.string(),
  }),
  event: z.object({
    id: z.string(),
    name: z.string(),
    startDateTime: z.string(),
    endDateTime: z.string(),
    location: z.enum(Object.values(API_SPONSORSHIP_EVENT_LOCATION)),
    venueName: z.string().nullable(),
    venueAddress: z.string().nullable(),
    venueCity: z.string().nullable(),
    venueState: z.string().nullable(),
    venueCountry: z.string().nullable(),
    status: z.enum(Object.values(EVENT_STATUS)),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
  }),
  plan: sponsorShipPlanSchema.nullable(),
})

export const sponsorSubEventListDisplaySchema = z.object({
  id: z.string(),
  subEventName: z.string(),
  eventName: z.string(),
  sponsorshipTier: z.string(),
  startDateTime: z.string(),
  endDateTime: z.string(),
  location: z.string(),
  sponsorshipStatus: z.enum(Object.values(DISPLAY_SPONSORSHIP_STATUS)),
  eventStatus: z.enum(Object.values(EVENT_STATUS)),
})

export const sponsorAdPlacementSchema = z.object({
  key: z.string(),
  placement: z.string(),
})

export const sponsorshipDetailsSchema = z.object({
  id: z.string(),
  status: sponsorShipAPIStatusSchema,
  amount: z.string().nullable(),
  event: z.object({
    id: z.string(),
    name: z.string(),
    startDateTime: z.string().transform(val => new Date(val)),
    endDateTime: z.string().transform(val => new Date(val)),
    location: z.enum(Object.values(API_SPONSORSHIP_EVENT_LOCATION)),
    venueName: z.string().nullable(),
    venueAddress: z.string().nullable(),
    venueCity: z.string().nullable(),
    venueState: z.string().nullable(),
    venueCountry: z.string().nullable(),
    status: z.enum(Object.values(EVENT_STATUS)),
  }),
  subEvent: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  plan: sponsorShipPlanSchema.nullable(),
  placements: z.array(sponsorAdPlacementSchema),
})

export type SponsorShipAPIStatusType = z.infer<
  typeof sponsorShipAPIStatusSchema
>
export type SponsorShipPlanType = z.infer<typeof sponsorShipPlanSchema>
export type SponsorAdPlacementType = z.infer<typeof sponsorAdPlacementSchema>
export type SponsorshipDetailsType = z.infer<typeof sponsorshipDetailsSchema>
export type SponsorEventListType = z.infer<typeof sponsorEventListSchema>
export type DisplaySponsorEvent = z.infer<typeof sponsorEventListDisplaySchema>
export type SponsorSubEventListType = z.infer<typeof sponsorSubEventListSchema>
export type DisplaySponsorSubEvent = z.infer<
  typeof sponsorSubEventListDisplaySchema
>

export interface SponsorShipPlan {
  id: string
  name: string
  price: string
  key: string
  status: string
  soldOut: boolean
  availability: {
    sold: number
    allowed: number
    available: number
  }
  heroDuration?: number
  availablePlacements: Array<{
    key: string
    placement: string
  }>
  description: string
  priority: number
  maxSponsorshipsLimit: number
}

export interface AdPlacementPlanType {
  id: string
  name: string
  benefits: string[]
  price: string
}

export const uploadAdSchema = z.object({
  adName: z
    .string()
    .trim()
    .min(1, 'Advertisement title is required')
    .refine(
      value => {
        const plainTextLength = getPlainTextLength(value)
        return plainTextLength > 0
      },
      { message: 'Advertisement title is required' }
    )
    .refine(
      value => {
        const plainTextLength = getPlainTextLength(value)
        return plainTextLength <= 52
      },
      { message: 'Maximum 52 characters allowed' }
    ),
  adDescription: z
    .string()
    .trim()
    .min(1, 'Advertisement description is required')
    .refine(
      value => {
        const plainTextLength = getPlainTextLength(value)
        return plainTextLength > 0
      },
      { message: 'Advertisement description is required' }
    )
    .refine(
      value => {
        const plainTextLength = getPlainTextLength(value)
        return plainTextLength <= 190
      },
      { message: 'Maximum 190 characters allowed' }
    ),
  adStartDate: z.date({ message: 'Start date is required' }),
  adEndDate: z.date({ message: 'End date is required' }),
  redirectionLink: z
    .string()
    .min(1, 'Redirection link is required')
    .max(255, 'Redirection link must be at most 255 characters')
    .regex(urlRegex, {
      message: 'Please enter a valid URL',
    }),
  linkText: z
    .string()
    .trim()
    .min(1, 'Link text is required')
    .max(40, 'Maximum 40 characters allowed'),
  adType: z.string().min(1, 'Ad type and placement is required'),
  sponsorLogo: z.any().optional(),
  eventImage: z.any().refine(file => file !== undefined && file !== null, {
    message: 'Event image is required',
  }),
  showTitle: z.boolean(),
  showDescription: z.boolean(),
})

export type UploadAdFormData = z.infer<typeof uploadAdSchema>

export const sponsorShipAdvertisementSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  startDate: z.date(),
  endDate: z.date(),
  placement: z.string(),
  status: z.enum(Object.values(AdvertisementStatus)),
  event: z.object({
    id: z.string(),
    name: z.string(),
  }),
  sponsor: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
  }),
  sponsorId: z.string(),
  sponsorshipId: z.string(),
  totalClicks: z.number(),
  totalImpressions: z.number(),
  ctr: z.number(),
  isChangesRequired: z.boolean(),
  subEvent: z.object({
    id: z.string(),
    name: z.string(),
  }),
})

export type SponsorShipAdvertisementType = z.infer<
  typeof sponsorShipAdvertisementSchema
>

export const availableADDateSchema = z.object({
  minDate: z.string(),
  maxDate: z.string(),
  bookedDates: z.array(z.string()),
  heroDuration: z.number().optional(),
})

export type AvailableADDatesType = z.infer<typeof availableADDateSchema>

export const advertisementMediaSchema = z.object({
  url: z.string(),
  expiresOn: z.date(),
  key: z.string(),
})

export const adPositions = z.object({
  x: z.number(),
  y: z.number(),
})

export const advertisementDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  showTitle: z.boolean(),
  showDescription: z.boolean(),
  startDate: z.date(),
  endDate: z.date(),
  link: z.string(),
  linkText: z.string(),
  sponsorLogo: advertisementMediaSchema,
  eventImage: advertisementMediaSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
  placement: z.string(),
  status: z.string(),
  event: z.object({
    id: z.string(),
    name: z.string(),
  }),
  sponsorId: z.string(),
  sponsorshipId: z.string(),
  isChangesRequired: z.boolean(),
  namePosition: adPositions,
  descriptionPosition: adPositions,
  sponsorLogoPosition: adPositions,
  linkPosition: adPositions,
})

export type AdvertisementData = z.infer<typeof advertisementDetailSchema>

export const editAdPlacementSchema = z.object({
  adType: z.string().min(1, 'Ad type and placement is required'),
  adStartDate: z.date({ message: 'Start date is required' }),
  adEndDate: z.date({ message: 'End date is required' }),
})
export type EditAdPlacementType = z.infer<typeof editAdPlacementSchema>

export const geographicDistributionItemSchema = z.object({
  region: z.string(),
  count: z.number(),
  percentage: z.number(),
})

export const ageDistributionSchema = z.object({
  '12-17': z.number(),
  '18-24': z.number(),
  '25-34': z.number(),
  '35-44': z.number(),
  '45-54': z.number(),
  '55+': z.number(),
})

export const sponsorshipAudienceAnalyticsSchema = z.object({
  geographicDistribution: z.array(geographicDistributionItemSchema),
  ageDistribution: ageDistributionSchema,
})

export type DemographicsRawData =
  | {
      data?: z.infer<typeof sponsorshipAudienceAnalyticsSchema>
    }
  | null
  | undefined

export type ParsedDemographicsData =
  | z.infer<typeof sponsorshipAudienceAnalyticsSchema>
  | null
  | undefined

const sponsor = z.object({
  id: z.string(),
  status: z.string(),
  subEventId: z.string(),
  sponsorId: z.string(),
  name: z.string(),
})

export const assignedEventSponsorData = z.object({
  event: z.object({
    id: z.string(),
    sponsors: z.array(sponsor),
  }),
  subEvents: z.array(
    z.object({
      id: z.string(),
      sponsors: z.array(sponsor),
    })
  ),
})

export type AssignedEventSponsorData = z.infer<typeof assignedEventSponsorData>

// Payload for assigning sponsors to event and sub-events
export const assignSponsorPayloadSchema = z.object({
  event: z.object({
    id: z.string(),
    sponsors: z.array(z.string()), // Array of sponsor IDs
  }),
  subEvent: z.array(
    z.object({
      id: z.string(),
      sponsors: z.array(z.string()), // Array of sponsor IDs
    })
  ),
})

export type AssignSponsorPayload = z.infer<typeof assignSponsorPayloadSchema>
